import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  IconButton,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useRbac } from '../../context/RbacContext';
import { useGetProductVariantsQuery } from '../../api/productDisplayApi';
import { Product, ProductConfig as ProductConfigType } from '../../types/product';

export interface ProductConfig {
  type: string;
  material: string;
  color: string;
  size: string;
  quantity: number;
  customization?: string;
  dimensions: {
    width: number;
    height: number;
    depth: number;
  };
  specifications?: Record<string, any>;
  specialRequests: string[];
}

interface ProductConfiguratorProps {
  product: Product;
  config: ProductConfig;
  onSave: (config: ProductConfig) => void;
}

export const ProductConfigurator = ({ product, config, onSave }: ProductConfiguratorProps) => {
  const { hasPermission } = useRbac();
  const [localConfig, setLocalConfig] = useState<ProductConfig>(config);
  const [error, setError] = useState<string | null>(null);
  const [availableMaterials, setAvailableMaterials] = useState<string[]>([]);
  const [availableColors, setAvailableColors] = useState<string[]>([]);

  const { data: productVariants } = useGetProductVariantsQuery(product.id);

  useEffect(() => {
    if (productVariants?.length) {
      const materials = Array.from(new Set(productVariants.map(v => v.material)));
      const colors = Array.from(new Set(productVariants.map(v => v.color)));
      setAvailableMaterials(materials);
      setAvailableColors(colors);
    }
  }, [productVariants]);

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    setLocalConfig((prev: ProductConfig) => ({ ...prev, type: event.target.value }));
  };

  const handleMaterialChange = (event: SelectChangeEvent<string>) => {
    setLocalConfig((prev: ProductConfig) => ({ ...prev, material: event.target.value }));
  };

  const handleColorChange = (event: SelectChangeEvent<string>) => {
    setLocalConfig((prev: ProductConfig) => ({ ...prev, color: event.target.value }));
  };

  const handleSizeChange = (event: SelectChangeEvent<string>) => {
    setLocalConfig((prev: ProductConfig) => ({ ...prev, size: event.target.value }));
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 1;
    setLocalConfig((prev: ProductConfig) => ({ ...prev, quantity: value }));
  };

  const handleAddSpecialRequest = () => {
    setLocalConfig({
      ...localConfig,
      specialRequests: [...(localConfig.specialRequests || []), '']
    });
  };

  const handleRemoveSpecialRequest = (index: number) => {
    const requests = [...(localConfig.specialRequests || [])];
    requests.splice(index, 1);
    setLocalConfig({
      ...localConfig,
      specialRequests: requests
    });
  };

  const handleSpecialRequestChange = (_: React.ChangeEvent<HTMLInputElement>, i: number, request: string) => {
    const requests = localConfig.specialRequests || [];
    requests[i] = request;
    setLocalConfig({
      ...localConfig,
      specialRequests: requests
    });
  };

  const handleCustomizationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalConfig((prev: ProductConfig) => ({
      ...prev,
      customization: e.target.value
    }));
  };

  const handleSubmit = () => {
    if (!hasPermission('products:configure')) {
      setError('You do not have permission to configure products');
      return;
    }
    onSave(localConfig);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom>
              Configure Product
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={localConfig.type}
                onChange={handleTypeChange}
                label="Type"
              >
                {product.types?.map((type: string) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Material</InputLabel>
              <Select
                value={localConfig.material}
                onChange={handleMaterialChange}
                label="Material"
              >
                {availableMaterials.map((material: string) => (
                  <MenuItem key={material} value={material}>
                    {material}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Color</InputLabel>
              <Select
                value={localConfig.color}
                onChange={handleColorChange}
                label="Color"
              >
                {availableColors.map((color: string) => (
                  <MenuItem key={color} value={color}>
                    {color}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Size</InputLabel>
              <Select
                value={localConfig.size}
                onChange={handleSizeChange}
                label="Size"
              >
                {product.sizes?.map((size: string) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={localConfig.quantity}
              onChange={handleQuantityChange}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Special Requests
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(localConfig.specialRequests || []).map((request, index) => (
                <Chip
                  key={index}
                  label={request}
                  onDelete={() => handleRemoveSpecialRequest(index)}
                  variant="outlined"
                />
              ))}
              <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={handleAddSpecialRequest}
              >
                Add Request
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Customization"
              multiline
              rows={4}
              value={localConfig.customization || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCustomizationChange(e)}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!hasPermission('product:config')}
              fullWidth
            >
              Save Configuration
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};
